import { formatText } from "lib/speech-synthesizer/format-text";
import { useCallback, useEffect, useState } from "react";

let recognition: any = null;
if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
  recognition = new (window.SpeechRecognition ||
    window.webkitSpeechRecognition)();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = "en-US";
  recognition.maxAlternatives = 1;
}

export const useSpeechToText = (prevValue: string) => {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);


  useEffect(() => {
    if (!recognition) {
      setError("Speech recognition isn't available on your device");
      return;
    }

    let finalTranscripts = "";
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscripts = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        transcript.replace("\n", "<br>");

        if (event.results[i].isFinal) {
          finalTranscripts += transcript;
        } else {
          interimTranscripts += transcript;
        }
        setText(`${finalTranscripts} ${interimTranscripts}`);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsListening(false);

      if (event.error) {
        console.error(`Error code: ${event.error}`);

        switch (event.error) {
          case "no-speech":
            setError("No speech detected.");
            break;
          case "audio-capture":
            setError(
              "No microphone detected or microphone permissions are blocked."
            );
            console.log(
              "No microphone detected or microphone permissions are blocked."
            );
            break;
          case "not-allowed":
            setError("Microphone access is denied by the user or browser.");
            break;
          case "network":
            setError("Network issue preventing recognition.");
            console.log("Network issue preventing recognition.");
            break;
          default:
            console.log(`Unhandled error: ${event.error}`);
        }
      }
      recognition.stop();
    };

    recognition.onspeechstart = () => {
      console.log("Speech has been detected");
      setIsListening(true);
    };

    recognition.onstart = () => {
      setMessage("Speech recognition started");
      setIsListening(true);
    };

    return () => {
      if (recognition) {
        recognition?.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!recognition) {
      setError("Speech recognition isn't available on your device");
      return;
    }

    recognition.onspeechend = async () => {
      recognition.stop();
      const { formattedText, error } = await formatText(text);
      setText(formattedText);
      setError(error);
      setIsListening(false);
      setMessage("Speech recognition has stopped.");
    };
  }, [text]);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
    if (message) {
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    }
  }, [error, message]);

  const startListening = useCallback(() => {
    if (isListening || !recognition) return;
    recognition.start();

    setText(prevValue);
    setError(null);
    setMessage(null);
  }, [isListening, prevValue]);

  const stopListening = useCallback(() => {
    if (recognition) {
      recognition.stop();
    }
  }, []);

  return {
    text,
    isListening,
    startListening,
    stopListening,
    error,
    message,
  };
};
